/**
 * Telefone é a chave de identidade do cliente: é por ele que a ficha criada
 * pelo barbeiro no balcão encontra a conta que a pessoa criou pelo link.
 *
 * Por isso ele é normalizado em E.164 antes de qualquer comparação — "(51)
 * 99999-8888", "51999998888" e "+55 51 99999 8888" precisam virar a mesma coisa.
 */

const DEFAULT_COUNTRY_CODE = '55';

export type PhoneParseResult =
  | { ok: true; e164: string }
  | { ok: false; reason: 'EMPTY' | 'TOO_SHORT' | 'TOO_LONG' | 'INVALID' };

function onlyDigits(value: string): string {
  return value.replace(/\D+/g, '');
}

/**
 * Normaliza para E.164. Sem código de país, assume Brasil — que é onde o
 * produto vive hoje.
 */
export function parsePhone(input: string, countryCode = DEFAULT_COUNTRY_CODE): PhoneParseResult {
  const raw = input.trim();
  if (raw.length === 0) return { ok: false, reason: 'EMPTY' };

  const hadPlus = raw.startsWith('+');
  let digits = onlyDigits(raw);

  if (digits.length === 0) return { ok: false, reason: 'INVALID' };

  if (!hadPlus) {
    // 10 dígitos = fixo com DDD, 11 = celular com DDD. Só nesses casos dá para
    // afirmar que falta o código do país.
    if (digits.length === 10 || digits.length === 11) {
      digits = countryCode + digits;
    } else if (digits.length === 8 || digits.length === 9) {
      // Número sem DDD: não dá para adivinhar a cidade do cliente.
      return { ok: false, reason: 'TOO_SHORT' };
    }
  }

  if (digits.length < 11) return { ok: false, reason: 'TOO_SHORT' };
  if (digits.length > 15) return { ok: false, reason: 'TOO_LONG' };

  return { ok: true, e164: `+${digits}` };
}

/** Igual a `parsePhone`, mas lança. Use nas bordas já validadas. */
export function normalizePhone(input: string, countryCode = DEFAULT_COUNTRY_CODE): string {
  const result = parsePhone(input, countryCode);
  if (!result.ok) throw new RangeError(`Telefone inválido: "${input}" (${result.reason})`);
  return result.e164;
}

export function isValidPhone(input: string, countryCode = DEFAULT_COUNTRY_CODE): boolean {
  return parsePhone(input, countryCode).ok;
}

/**
 * Formata para leitura humana. Números brasileiros saem como (51) 99999-8888;
 * o resto sai como veio, porque inventar máscara de país que não conhecemos
 * atrapalha mais do que ajuda.
 */
export function formatPhone(e164: string): string {
  const digits = onlyDigits(e164);

  if (digits.startsWith(DEFAULT_COUNTRY_CODE) && (digits.length === 12 || digits.length === 13)) {
    const area = digits.slice(2, 4);
    const rest = digits.slice(4);
    const split = rest.length === 9 ? 5 : 4;
    return `(${area}) ${rest.slice(0, split)}-${rest.slice(split)}`;
  }

  return e164;
}

/** Formato aceito pelo link wa.me: só dígitos, com código do país. */
export function toWhatsAppNumber(e164: string): string {
  return onlyDigits(e164);
}
